import { Head, Link } from "@inertiajs/react"
import { Building2, ExternalLink, Users } from "lucide-react"

import AppLayout from "@/layouts/app-layout"
import { companyPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Company {
  id: number
  name: string
  tier: number | null
  tier_name: string
  tier_description: string
  score: number | null
  developer_count: number
  logo_url: string | null
}

interface Props {
  companies: Company[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Companies", href: "/companies" },
]

export default function CompaniesIndex({ companies }: Props) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Companies" />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Companies</h1>
            <p className="text-muted-foreground">
              Companies where Argentinian developers work
            </p>
          </div>
        </div>

        <div className="rounded-xl border">
          <div className="border-b p-4">
            <h2 className="font-semibold">All Companies</h2>
          </div>

          {companies.length === 0 ? (
            <div className="text-muted-foreground p-8 text-center">
              No companies found.
            </div>
          ) : (
            <div className="divide-y">
              {companies.map((company) => (
                <div
                  key={company.id}
                  className="hover:bg-muted/50 flex items-center gap-4 p-4"
                >
                  {company.logo_url ? (
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white p-1">
                      <img
                        src={company.logo_url}
                        alt={`${company.name} logo`}
                        className="h-full w-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = "none"
                          e.currentTarget.nextElementSibling?.classList.remove(
                            "hidden",
                          )
                        }}
                      />
                      <div className="bg-primary/10 text-primary hidden h-10 w-10 items-center justify-center rounded-full">
                        <Building2 className="h-5 w-5" />
                      </div>
                    </div>
                  ) : (
                    <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-full">
                      <Building2 className="h-5 w-5" />
                    </div>
                  )}

                  <div className="flex-1">
                    <Link
                      href={companyPath(company.id)}
                      className="font-medium hover:underline"
                    >
                      {company.name}
                    </Link>
                    <p className="text-muted-foreground text-sm">
                      {company.tier_description}
                    </p>
                    <span className="bg-primary/10 text-primary mt-1 inline-block rounded-full px-2 py-0.5 text-xs">
                      Tier {company.tier_name}
                    </span>
                  </div>

                  <div className="text-right">
                    <div className="text-lg font-bold">
                      {Math.round(company.score ?? 0)}
                    </div>
                    <div className="text-muted-foreground flex items-center gap-1 text-xs">
                      <Users className="h-3 w-3" />
                      {company.developer_count}
                    </div>
                  </div>

                  <Link
                    href={companyPath(company.id)}
                    className="text-muted-foreground hover:text-primary"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
